import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined"
import Chip from "@mui/material/Chip"
import Divider from "@mui/material/Divider"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import SwipableDrawer from "@mui/material/SwipeableDrawer"
import Typography from "@mui/material/Typography"
import { useTheme } from "@mui/material/styles"
import { useSelector } from "react-redux"
import { Link as RouterLink } from "react-router-dom"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import UserAvatar from "../../../common/components/UserAvatar"
import { trimLongString } from "../../../common/utils"
import OneBaseLogo from "../../../static/images/onebase-logo.png"
import {
  selectFullName,
  selectProfile,
  selectProfileLoaded,
} from "../../users/reducer"
import { useLogoutUser } from "../utils"
import {
  PROFILE_MENU_URL,
  getSidebarBottomLinks,
  getSidebarLinkGroups,
  useIsPathActive,
  useSidebarBadgeCounts,
} from "./SidebarLinks"
import GlobalSearchField from "./global-search-field/GlobalSearchField"

export default function MobileSidebarLinksDrawer({ open, onClose }) {
  const fullName = useSelector(selectFullName)
  const profile = useSelector(selectProfile)
  const profileLoaded = useSelector(selectProfileLoaded)
  const logoutUser = useLogoutUser()
  const theme = useTheme()
  const badgeCounts = useSidebarBadgeCounts()
  const linkGroups = [
    ...getSidebarLinkGroups(profile),
    { label: null, links: getSidebarBottomLinks(profile) },
  ]

  return (
    <SwipableDrawer
      open={open}
      onOpen={() => {}}
      onClose={onClose}
      sx={{
        "& .MuiPaper-root": {
          width: "80%",
          maxWidth: "330px",
          borderRadius: "0 16px 16px 0",
          "&:before": {
            display: "none",
          },
          "& .MuiBox-root": {
            background: "transparent",
            boxShadow: "none",
            backdropFilter: "none",
          },
        },
      }}
    >
      <Column sx={{ p: 2, justifyContent: "space-between", height: "100%" }}>
        <Column>
          <Row sx={{ alignItems: "center" }}>
            <Link component={RouterLink} to="/" onClick={onClose}>
              <img
                src={OneBaseLogo}
                style={{
                  width: "120px",
                }}
                alt="OneBase"
              />
            </Link>
          </Row>
          <Row sx={{ mt: 2 }}>
            <GlobalSearchField
              width="100%"
              maxWidth="100%"
              onSearchResultSelected={onClose}
            />
          </Row>
          <Column sx={{ mt: 3, mb: 3, gap: 2 }}>
            {linkGroups.map((group, i) => (
              <Column key={group.label || i} sx={{ gap: 0.5 }}>
                {group.label ? (
                  <Typography
                    variant="caption"
                    sx={{
                      px: 1.5,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: theme.palette.colors.gray500,
                    }}
                  >
                    {group.label}
                  </Typography>
                ) : (
                  <Divider sx={{ mb: 1 }} />
                )}
                {group.links.map((data) => (
                  <NavLink
                    key={data.url}
                    linkData={data}
                    badgeCount={badgeCounts[data.badgeKey]}
                    onClicked={onClose}
                  />
                ))}
              </Column>
            ))}
          </Column>
        </Column>
        <Row
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            py: 3,
            borderTop: `1px solid ${theme.palette.colors.gray200}`,
          }}
        >
          <Link
            component={RouterLink}
            to={PROFILE_MENU_URL}
            onClick={onClose}
            title="My profile"
            underline="none"
            color="text.primary"
            sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
          >
            <UserAvatar width="32px" fontSize="16px" />
            <Column sx={{ ml: 2 }}>
              <Typography variant="h5">{fullName}</Typography>
              <Typography
                variant="body1"
                sx={{ color: theme.palette.colors.gray600 }}
              >
                {profileLoaded ? trimLongString(profile.email, 23) : ""}
              </Typography>
            </Column>
          </Link>
          <IconButton onClick={logoutUser} aria-label="Log out">
            <LogoutOutlinedIcon sx={{ color: theme.palette.colors.gray500 }} />
          </IconButton>
        </Row>
      </Column>
    </SwipableDrawer>
  )
}

function NavLink({ linkData, badgeCount, onClicked }) {
  const isActive = useIsPathActive(linkData.url)
  const theme = useTheme()
  return (
    <Link
      component={RouterLink}
      to={linkData.url}
      sx={{
        display: "flex",
        alignItems: "center",
        px: 1.5,
        py: 1,
        backgroundColor: isActive ? theme.palette.colors.gray50 : "inherit",
        borderRadius: theme.shape.borderRadius,
      }}
      onClick={onClicked}
      {...linkData.getLinkProps()}
    >
      <linkData.icon sx={{ color: theme.palette.colors.gray500 }} />
      <Typography
        variant="h6"
        sx={{ ml: 1, color: theme.palette.colors.gray800 }}
      >
        {linkData.title}
      </Typography>
      {badgeCount ? (
        <Chip
          size="small"
          color="secondary"
          label={badgeCount > 99 ? "99+" : badgeCount}
          sx={{ ml: "auto", height: 20, fontSize: "11px", fontWeight: 700 }}
        />
      ) : null}
    </Link>
  )
}
